import React, { useEffect, useRef, useState, useCallback } from 'react';
import Loading from '@/components/ui/loading';
export interface InfiniteScrollProps {
  /**
   * 是否还有更多数据
   */
  hasMore: boolean;
  /**
   * 加载更多的函数
   */
  loadMore: () => Promise<void>;
  /**
   * 没有更多数据时的文本
   */
  noMoreText?: string;
  /**
   * 自定义加载中的内容
   */
  children?: (hasMore: boolean, failed: boolean, retry: () => void) => React.ReactNode;
  /**
   * 自定义类名
   */
  className?: string;
  /**
   * 是否禁用
   */
  disabled?: boolean;
  /**
   * 根元素，用于 Intersection Observer
   */
  root?: Element | null;
  /**
   * 根边距，用于 Intersection Observer
   */
  rootMargin?: string;
}

/**
 * 自定义无限滚动组件
 * 使用 Intersection Observer API 实现
 * 参考 antd-mobile 的 InfiniteScroll 设计
 */
const InfiniteScroll: React.FC<InfiniteScrollProps> = ({
  hasMore,
  loadMore,
  noMoreText,
  children,
  className,
  disabled = false,
  root = null,
  rootMargin = '0px',
}) => {
  const [failed, setFailed] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const loadingRef = useRef(false);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const pendingLoadRef = useRef(false); // 标记是否有待处理的加载请求（在加载期间触发的）
  const hasMoreRef = useRef(hasMore); // 使用 ref 保存最新的 hasMore 值
  const disabledRef = useRef(disabled); // 使用 ref 保存最新的 disabled 值
  const loadMoreRef = useRef(loadMore); // 使用 ref 保存最新的 loadMore 函数
  const handleLoadMoreRef = useRef<(() => Promise<void>) | null>(null); // 使用 ref 保存 handleLoadMore 函数

  // 保持 ref 值最新
  useEffect(() => {
    const prevHasMore = hasMoreRef.current;
    hasMoreRef.current = hasMore;

    // 当 hasMore 从 false 变为 true 时，如果触发元素已经在视口内，立即触发加载
    if (!prevHasMore && hasMore && !disabledRef.current && !loadingRef.current) {
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        const rootElement = root || (typeof window !== 'undefined' ? window : null);
        const viewportHeight = rootElement instanceof Element
          ? rootElement.getBoundingClientRect().height
          : (window.innerHeight || document.documentElement.clientHeight);

        // 检查触发元素是否在视口内
        const isInViewport = rootElement instanceof Element
          ? rect.top >= 0 && rect.top <= viewportHeight
          : rect.top < viewportHeight;

        if (isInViewport) {
          // 使用 requestAnimationFrame 确保在下一个渲染周期执行
          requestAnimationFrame(() => {
            if (hasMoreRef.current && !disabledRef.current && !loadingRef.current && handleLoadMoreRef.current) {
              handleLoadMoreRef.current();
            }
          });
        }
      }
    }
  }, [hasMore, root]);

  useEffect(() => {
    disabledRef.current = disabled;
  }, [disabled]);

  useEffect(() => {
    loadMoreRef.current = loadMore;
  }, [loadMore]);

  const handleLoadMore = useCallback(async () => {
    if (loadingRef.current || !hasMoreRef.current || disabledRef.current) {
      return;
    }

    loadingRef.current = true;
    setFailed(false);
    pendingLoadRef.current = false; // 重置待处理标志

    try {
      await loadMoreRef.current();

      // 加载完成后，检查是否还需要继续加载
      // 这可以处理快速滚动时，最后一次触发被忽略的情况
      // 使用 requestAnimationFrame 确保 DOM 已更新后再检查
      requestAnimationFrame(() => {
        if (observerRef.current && triggerRef.current && hasMoreRef.current && !disabledRef.current && !loadingRef.current && handleLoadMoreRef.current) {
          const entries = observerRef.current.takeRecords();
          if (entries.length > 0) {
            const [entry] = entries;
            if (entry.isIntersecting) {
              // 元素仍在视窗内，继续加载
              handleLoadMoreRef.current();
              return;
            }
          }

          // 如果 takeRecords 没有返回结果，或者 entry 不在视窗内，手动检查
          // 这可以处理快速滚动时，Intersection Observer 还没来得及更新状态的情况
          const rect = triggerRef.current.getBoundingClientRect();
          const rootElement = root || (typeof window !== 'undefined' ? window : null);
          const viewportHeight = rootElement instanceof Element
            ? rootElement.getBoundingClientRect().height
            : (window.innerHeight || document.documentElement.clientHeight);

          const isInViewport = rootElement instanceof Element
            ? rect.top >= 0 && rect.top <= viewportHeight
            : rect.top < viewportHeight;

          if (isInViewport) {
            // 延迟一小段时间后再次检查，确保 Intersection Observer 有机会更新
            setTimeout(() => {
              if (observerRef.current && triggerRef.current && hasMoreRef.current && !disabledRef.current && !loadingRef.current && handleLoadMoreRef.current) {
                const entries = observerRef.current.takeRecords();
                if (entries.length > 0) {
                  const [entry] = entries;
                  if (entry.isIntersecting) {
                    handleLoadMoreRef.current();
                    return;
                  }
                }

                // 再次手动检查
                const rect = triggerRef.current.getBoundingClientRect();
                const rootElement = root || (typeof window !== 'undefined' ? window : null);
                const viewportHeight = rootElement instanceof Element
                  ? rootElement.getBoundingClientRect().height
                  : (window.innerHeight || document.documentElement.clientHeight);

                const isInViewport = rootElement instanceof Element
                  ? rect.top >= 0 && rect.top <= viewportHeight
                  : rect.top < viewportHeight;

                if (isInViewport && handleLoadMoreRef.current) {
                  handleLoadMoreRef.current();
                }
              }
            }, 100);
          }
        }
      });
    } catch (error) {
      console.error('Load more failed:', error);
      setFailed(true);
      pendingLoadRef.current = false;
    } finally {
      loadingRef.current = false;
    }
  }, [root]); // 只保留 root 作为依赖，其他使用 ref 访问

  // 保持 handleLoadMoreRef 最新
  useEffect(() => {
    handleLoadMoreRef.current = handleLoadMore;
  }, [handleLoadMore]);

  const handleRetry = useCallback(() => {
    setFailed(false);
    handleLoadMore();
  }, [handleLoadMore]);

  // 使用 Intersection Observer 监听触发元素
  useEffect(() => {
    if (!triggerRef.current) return;

    const trigger = triggerRef.current;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        // 使用 ref 中的最新值，而不是闭包中的值
        if (entry.isIntersecting && hasMoreRef.current && !disabledRef.current) {
          if (loadingRef.current) {
            // 如果正在加载，标记有待处理的请求
            pendingLoadRef.current = true;
          } else if (handleLoadMoreRef.current) {
            // 如果不在加载，直接触发加载
            handleLoadMoreRef.current();
          }
        }
      },
      {
        root,
        rootMargin,
        threshold: 0,
      }
    );

    observer.observe(trigger);
    observerRef.current = observer;

    return () => {
      observer.disconnect();
    };
  }, [root, rootMargin]); // 移除 hasMore、disabled 和 handleLoadMore 依赖，使用 ref 访问最新值

  // 默认的渲染内容
  const defaultChildren = (hasMore: boolean, failed: boolean, retry: () => void) => {
    if (failed) {
      return (
        <div
          className="flex items-center justify-center p-4 text-red-500 text-sm cursor-pointer transition-opacity duration-200 hover:opacity-80"
          onClick={retry}
        >
          load failed, click to retry
        </div>
      );
    }

    if (hasMore) {
      return (
        <Loading height="50px" />
      );
    }

    return (
      <>
        {
          !!noMoreText && (
            <div className="flex items-center justify-center p-4 text-tertiary text-ss">
              {noMoreText}
            </div>
          )
        }
      </>
    );
  };

  return (
    <div className={`w-full ${className || ''}`}>
      {children ? children(hasMore, failed, handleRetry) : defaultChildren(hasMore, failed, handleRetry)}
      {/* Intersection Observer 触发元素 */}
      <div
        ref={triggerRef}
        className="h-px w-full pointer-events-none"
      />
    </div>
  );
};

export default InfiniteScroll;
